import { useCallback, useEffect, useRef, useState } from 'react';
import type { DragEvent, HTMLAttributes, ReactNode, Ref } from 'react';

import { runWithRetry } from '@/utils/runWithRetry';

import { AdjustPhotoModal, PhotoUploadOverlay } from './AdjustPhotoModal';

export type PhotoShape = 'round' | 'rect';

export type UsePhotoUploadOptions = {
  /** `round` crops 1:1 in a circle (people), `rect` crops 16:9 (vessels, covers). */
  shape: PhotoShape;
  /**
   * Gets the adjusted photo on Done. A returned promise is the upload: the photo shows as pending
   * while it runs, and a rejection brings back an error toast with Retry. Returning nothing means
   * the caller only holds the file — a form that uploads it once the item exists.
   */
  onPhoto: (file: File) => Promise<unknown> | void;
  /** The error toast's text; "Couldn't upload the photo" by default. */
  errorMessage?: string;
  /** The hidden file input, for callers that open the picker with `ref.current.click()`. */
  inputRef?: Ref<HTMLInputElement>;
  /** The adjust dialog was dismissed without Done. */
  onCancel?: () => void;
};

export type PhotoUpload = {
  /** Opens the system file picker. Call it inside the click handler — iOS refuses it later. */
  pick: () => void;
  /** Starts the adjust step from a file already in hand. Anything but an image is ignored. */
  take: (file?: File | null) => void;
  /** The adjusted photo while its upload runs, to show in place of the current one. */
  preview: string | null;
  pending: boolean;
  /** Spread on the box the photo sits in so a file can be dropped on it. */
  dropProps: Pick<HTMLAttributes<HTMLElement>, 'onDragOver' | 'onDragLeave' | 'onDrop'>;
  /**
   * The hidden input and the adjust dialog. Render it once, outside any element with an `onClick`:
   * clicks in a portal still bubble through the React tree and would open the picker again.
   */
  element: ReactNode;
  /** The spinner while uploading and the drop highlight; render inside the photo's positioned box. */
  overlay: ReactNode;
};

/**
 * The one way a photo gets onto a vessel, a person or an event: pick or drop a file, adjust it,
 * press Done — and it is saved, with no second Save to forget. Screens keep their own photo and
 * trigger and wire this behind them; `LogoPicker` and `AvatarUploader` are built on it.
 */
export function usePhotoUpload({
  shape,
  onPhoto,
  errorMessage,
  inputRef,
  onCancel,
}: UsePhotoUploadOptions): PhotoUpload {
  const input = useRef<HTMLInputElement | null>(null);
  const [source, setSource] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const pending = preview !== null;

  useEffect(() => () => void (source && URL.revokeObjectURL(source)), [source]);
  useEffect(() => () => void (preview && URL.revokeObjectURL(preview)), [preview]);

  const setInput = useCallback(
    (node: HTMLInputElement | null) => {
      input.current = node;
      if (typeof inputRef === 'function') {
        inputRef(node);
      } else if (inputRef) {
        inputRef.current = node;
      }
    },
    [inputRef],
  );

  const take = useCallback((file?: File | null) => {
    if (file?.type.startsWith('image/')) {
      setSource(URL.createObjectURL(file));
    }
  }, []);

  const attempt = async (file: File) => {
    const upload = onPhoto(file);
    if (!upload) {
      return;
    }
    setPreview(URL.createObjectURL(file));
    try {
      await upload;
    } finally {
      setPreview(null);
    }
  };

  const isFileDrag = (e: DragEvent<HTMLElement>) => e.dataTransfer.types.includes('Files');

  const dropProps: PhotoUpload['dropProps'] = {
    onDragOver: (e) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      setDragOver(!pending);
    },
    onDragLeave: (e) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragOver(false);
    },
    onDrop: (e) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      setDragOver(false);
      if (!pending) take(e.dataTransfer.files[0]);
    },
  };

  const element = (
    <>
      <input
        ref={setInput}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          take(e.target.files?.[0]);
          // The same file picked again must still fire `change`.
          e.target.value = '';
        }}
      />
      {source && (
        <AdjustPhotoModal
          src={source}
          shape={shape}
          onCancel={() => {
            setSource(null);
            onCancel?.();
          }}
          onDone={(file) => {
            setSource(null);
            void runWithRetry(() => attempt(file), errorMessage);
          }}
        />
      )}
    </>
  );

  return {
    pick: () => {
      if (!pending) input.current?.click();
    },
    take,
    preview,
    pending,
    dropProps,
    element,
    overlay: <PhotoUploadOverlay pending={pending} dragOver={dragOver} />,
  };
}
